import { useState } from "react"
import {
  Button,
  FormField,
  Heading,
  RadioGroup,
  Text,
  TextField,
} from "@smooth/design-system-components"
import { evaluationError } from "../errors"
import type {
  EvaluationCase,
  ImportCustomer,
  ImportSource,
  ImportTimeline,
  Terminals,
} from "../types"
import { shiftIso } from "../types"
import { fromLocalInput } from "./ScenarioRecordsForm"
import { CUT_AFTER_MS, ImportTimelinePicker, type TimelinePoint } from "./ImportTimelinePicker"
import { AlertMessage, Section, StatusMessage } from "./parts"

export type Method = "blank" | "copy" | "import"
type SourceKind = "dunningCaseId" | "userId" | "generationRequestId"

const METHODS: { value: Method; label: string }[] = [
  { value: "blank", label: "空から作る" },
  { value: "copy", label: "検証ケースを複製" },
  { value: "import", label: "督促ケースから読み込む" },
]
const SOURCE_KINDS: { value: SourceKind; label: string }[] = [
  { value: "dunningCaseId", label: "督促ケース ID" },
  { value: "userId", label: "ユーザー ID" },
  { value: "generationRequestId", label: "生成リクエスト ID" },
]
const SOURCE_ID_LABELS: Record<SourceKind, string> = {
  dunningCaseId: "読み込む督促ケースの ID",
  userId: "読み込むユーザーの ID",
  generationRequestId: "読み込む生成リクエストの ID",
}
/** 複製の候補として一度に出す件数 */
const MAX_CANDIDATES = 8

type Props = {
  cases: EvaluationCase[]
  terminals: Terminals
  busy: boolean
  onBlank: () => void
  /** 既存の「複製」と同じく、コピーを保存して開く */
  onCopy: (record: EvaluationCase) => Promise<void>
  /** 読み込んで作成フォームに入れる（保存はしない） */
  onImport: (source: ImportSource) => Promise<void>
  /** 読み込む時点を選ぶための会話と生成の記録 */
  onLoadTimeline: (customer: ImportCustomer) => Promise<ImportTimeline>
  /** 選んだ時点ごとに、期待する対応を付けてケースを保存する */
  onImportPoints: (customer: ImportCustomer, points: TimelinePoint[]) => Promise<void>
  /** 最初に選んでおく作り方（お客様の流れでは督促ケースからの読み込み） */
  defaultMethod?: Method
  /** 督促ケース ID を入れておく（督促ケース詳細の「検証で追う」から開いたとき） */
  defaultDunningCaseId?: number | null
}

/** 「ケースを作成」で開く、作り方の選択（空から／検証ケースを複製／督促ケースから読み込む） */
export function CaseCreator({
  cases,
  terminals,
  busy,
  onBlank,
  onCopy,
  onImport,
  onLoadTimeline,
  onImportPoints,
  defaultMethod = "blank",
  defaultDunningCaseId,
}: Props) {
  const [method, setMethod] = useState<Method>(defaultMethod)
  const [query, setQuery] = useState("")
  const [copyId, setCopyId] = useState("")
  const [sourceKind, setSourceKind] = useState<SourceKind>("dunningCaseId")
  const [sourceId, setSourceId] = useState(defaultDunningCaseId ? String(defaultDunningCaseId) : "")
  // 基準日時（datetime-local の値。空欄は今）
  const [asOf, setAsOf] = useState("")
  const [timeline, setTimeline] = useState<{
    customer: ImportCustomer
    data: ImportTimeline
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false)
  const disabled = busy || working

  const keyword = query.trim().replace(/^#/, "").toLowerCase()
  const candidates = cases
    .filter(
      (record) =>
        !keyword || String(record.id) === keyword || record.title.toLowerCase().includes(keyword)
    )
    .slice(0, MAX_CANDIDATES)
  const chosen = cases.find((record) => String(record.id) === copyId)
  const parsedId = /^\d+$/.test(sourceId.trim()) ? Number(sourceId.trim()) : null
  const byCustomer = sourceKind !== "generationRequestId"
  const customer: ImportCustomer | null = !parsedId
    ? null
    : sourceKind === "userId"
      ? { userId: parsedId }
      : sourceKind === "dunningCaseId"
        ? { dunningCaseId: parsedId }
        : null

  const run = async (action: () => Promise<void>) => {
    setWorking(true)
    setError(null)
    try {
      await action()
    } catch (e) {
      setError(evaluationError(e))
    } finally {
      setWorking(false)
    }
  }
  const load = () => {
    if (!parsedId) return
    if (!customer) {
      void run(() => onImport({ generationRequestId: parsedId }))
      return
    }
    void run(() => onImport(asOf ? { ...customer, asOf: fromLocalInput(asOf) } : customer))
  }
  const showTimeline = () => {
    if (!customer) return
    void run(async () => setTimeline({ customer, data: await onLoadTimeline(customer) }))
  }

  return (
    <div className="flex flex-col">
      <Heading level={2}>ケースを作成</Heading>
      <Section label="作り方">
        <FormField label="作り方">
          <RadioGroup
            name="evaluation-create-method"
            aria-label="作り方"
            options={METHODS}
            value={method}
            onChange={(value) => {
              setMethod(value as Method)
              setError(null)
            }}
          />
        </FormField>
      </Section>

      {method === "blank" && (
        <Section label="空から作る">
          <Text>架空の契約状況と会話を一から入力します。</Text>
          <div>
            <Button onClick={onBlank} disabled={disabled}>
              作成フォームを開く
            </Button>
          </div>
        </Section>
      )}

      {method === "copy" && (
        <Section label="検証ケースを複製">
          <FormField label="複製する検証ケースを探す">
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ID（例：12）またはタイトル"
            />
          </FormField>
          {candidates.length > 0 ? (
            <RadioGroup
              name="evaluation-copy-source"
              aria-label="複製する検証ケース"
              options={candidates.map((record) => ({
                value: String(record.id),
                label: `#${record.id} ${record.title}`,
              }))}
              value={copyId}
              onChange={setCopyId}
            />
          ) : (
            <Text size="sm">一致する検証ケースがありません。</Text>
          )}
          <Text size="sm">
            コピーを「（コピー）」付きのタイトルで保存して開きます。生成結果・評価は引き継ぎません。
          </Text>
          <div>
            <Button
              onClick={() => chosen && void run(() => onCopy(chosen))}
              disabled={disabled || !chosen}
            >
              複製する
            </Button>
          </div>
        </Section>
      )}

      {method === "import" && (
        <Section label="督促ケースから読み込む">
          <Text>
            実際の督促ケースで、AIドラフトの生成に使う入力（契約・金額・会話・メモ・交渉記録）を読み込み、作成フォームに入れます。保存するまで検証ケースにはなりません。
          </Text>
          <FormField label="読み込み元">
            <RadioGroup
              name="evaluation-import-kind"
              aria-label="読み込み元"
              appearance="chip"
              options={SOURCE_KINDS}
              value={sourceKind}
              onChange={(value) => {
                setSourceKind(value as SourceKind)
                setTimeline(null)
              }}
            />
          </FormField>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48">
              <FormField label={SOURCE_ID_LABELS[sourceKind]}>
                <TextField
                  inputMode="numeric"
                  value={sourceId}
                  onChange={(e) => {
                    setSourceId(e.target.value)
                    setTimeline(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") load()
                  }}
                />
              </FormField>
            </div>
            {byCustomer && (
              <div className="w-60">
                <FormField label="基準日時（日本時間・空欄は今）">
                  <TextField
                    type="datetime-local"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                </FormField>
              </div>
            )}
            <Button onClick={load} disabled={disabled || !parsedId}>
              読み込む
            </Button>
            {byCustomer && (
              <Button variant="outline" onClick={showTimeline} disabled={disabled || !customer}>
                会話から時点を選ぶ
              </Button>
            )}
          </div>
          {byCustomer ? (
            <Text size="sm">
              基準日時を指定すると、会話・約束・入金・交渉記録・メモをその時点の状態に組み立て直します。遅延損害金・勤務先・給料日・LINEブロックなど、その時点に戻せない項目は今の値のままです（読み込んだ後に一覧を出します）。
            </Text>
          ) : (
            <Text size="sm">
              本番で AI ドラフトを生成したときに AI へ渡した入力を、そのまま読み込みます。
            </Text>
          )}
          <Text size="sm">
            氏名・電話番号・勤務先名・メールアドレス・LINE名などは架空の値に置き換えます。読み込んだ後、自由記述に個人情報が残っていないか確認してください。
          </Text>
          {timeline && (
            <ImportTimelinePicker
              timeline={timeline.data}
              terminals={terminals}
              busy={disabled}
              onCut={(at) =>
                void run(() => onImport({ ...timeline.customer, asOf: shiftIso(at, CUT_AFTER_MS) }))
              }
              onCreatePoints={(points) => void run(() => onImportPoints(timeline.customer, points))}
              onGenerationRequest={(id) => void run(() => onImport({ generationRequestId: id }))}
            />
          )}
        </Section>
      )}

      {working && (
        <StatusMessage>
          {method === "import" ? "読み込んでいます…" : "処理しています…"}
        </StatusMessage>
      )}
      {error && <AlertMessage>{error}</AlertMessage>}
    </div>
  )
}
